import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useDispatch } from 'react-redux'
import { useEffect } from 'react'
import { IShopResponse } from '@cbgms/api'
import { getShop as getShopAction } from 'app/shops/state/actions'
import { GET_SHOP_SUCCESS } from 'app/shops/state/types'

const SHOP_DATA_QUERY_KEY = ['SHOP', 'DATA'] as const

/**
 * React Query hook to fetch current shop data with automatic deduplication.
 * Replaces Redux action getShop to prevent duplicate API calls.
 */
export const useShopData = (shopID: string) => {
  const dispatch = useDispatch()

  const query = useQuery({
    queryKey: [...SHOP_DATA_QUERY_KEY, shopID],
    queryFn: async (): Promise<IShopResponse['Data']> => {
      const result = await dispatch(getShopAction(shopID))
      return result.Data
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    enabled: !!shopID // Only run if shopID is provided
  })

  // Keep Redux (auth.shop) seeded from the shop data on every mount.
  // The queryFn short-circuits on cache hits and therefore does not dispatch getShop(),
  // so without this auth.shop is never refreshed when switching to a recently-viewed shop.
  // That leaves the main-content gate (auth.shop.ID === auth.currentShop) false, rendering the
  // sidebar but a blank main area, and can leave stale shop permissions in place. Dispatching the
  // success action here is idempotent and does not trigger an extra network request. See GMSWB-817.
  const { data } = query
  useEffect(() => {
    if (data) {
      dispatch({ type: GET_SHOP_SUCCESS, payload: { data: { Data: data } } })
    }
  }, [data, dispatch])

  return query
}

/**
 * Hook to refresh shop data
 */
export const useRefreshShopData = () => {
  const queryClient = useQueryClient()

  return (shopID: string) => {
    queryClient.invalidateQueries({
      queryKey: [...SHOP_DATA_QUERY_KEY, shopID]
    })
  }
}
